/**
 * Bench row view — one tough solution, its chains laid out as pipelines.
 *
 * The layout mirrors what actually ran:
 *
 *   student work → [extract_visuals] → [analyze_visual_*] → [score_*]
 *
 * Each stage takes its own verdict, because "the assessment is wrong" and "the
 * extraction dropped a line" are different findings with different fixes and
 * different answers for which model to buy.
 * See docs/FeatureDesigns/Evals/assessment-eval-bench.md §5.1, §7.1.
 */

import { useEffect, useState } from 'react';
import { LatexText } from '@shared-ui/latex';
import { LabelForm } from './LabelForm';
import { useMutation, useQuery } from '@tanstack/react-query';
import { adminApi } from '../../api/adminApi';
import type {
    MatrixRow, MatrixCell, MatrixChain, VerdictValue, StepRole,
} from '../../types/scenarioEvals';
import { tagsForRole } from '../../types/scenarioEvals';
import './BenchTab.css';

export function BenchRowView({ datasetId, row, focusOutputId, onClose, onChanged }: {
    datasetId: string;
    row: MatrixRow;
    focusOutputId?: string;
    onClose: () => void;
    onChanged: () => void;
}) {
    const q = row.input.question;
    const sol = row.input.studentSolution;
    const [zoomed, setZoomed] = useState<string | null>(null);
    const [labeling, setLabeling] = useState(false);

    // Close on Escape — this is a reading surface, you leave it constantly.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            // Innermost first: Escape out of the preview shouldn't also throw
            // away the row you were reading.
            if (zoomed) setZoomed(null);
            else onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose, zoomed]);

    return (
        <div className="bench-modal-backdrop" onClick={onClose}>
            <div className="bench-drawer" onClick={e => e.stopPropagation()}>
                <div className="bench-drawer-head">
                    <div>
                        <h2><LatexText text={q?.text?.slice(0, 160) || q?.id || ''} /></h2>
                        <div className="bench-row-meta">
                            {q?.subject && <span className="bench-pill">{q.subject}</span>}
                            {q?.questionType && <span className="bench-pill">{q.questionType}</span>}
                            {q?.correctAnswer && (
                                <span className="bench-pill">answer: <LatexText text={q.correctAnswer} /></span>
                            )}
                            {(row.input.difficultyTags ?? []).map(t => <span key={t} className="bench-tag">{t}</span>)}
                        </div>
                        {row.input.curationNote && <p className="bench-muted">{row.input.curationNote}</p>}
                    </div>
                    <button className="btn btn-secondary" onClick={onClose}>Close</button>
                </div>

                <div className="bench-drawer-body">
                    <aside className="bench-work">
                        <h4>Student work</h4>
                        {sol?.imageUrl
                            ? <button type="button" className="bench-work-zoom"
                                title="Click to view full size"
                                onClick={() => setZoomed(sol.imageUrl!)}>
                                <img src={sol.imageUrl} alt="student solution" />
                            </button>
                            : <pre className="bench-worktext">{sol?.textAnswer || sol?.selectedOption || '(no answer)'}</pre>}
                        {sol?.timeTakenSeconds !== undefined && (
                            <p className="bench-muted">{sol.timeTakenSeconds}s</p>
                        )}
                        {/* Ground truth belongs beside the work: the expected
                            assessment cannot honestly be written on a screen
                            that doesn't show the solution being assessed. */}
                        <div className="bench-label">
                            <div className="bench-label-head">
                                <h4>Ground truth</h4>
                                <button type="button" className="bench-linkbtn" onClick={() => setLabeling(v => !v)}>
                                    {labeling ? 'Close' : row.input.label ? 'Edit' : 'Add'}
                                </button>
                            </div>

                            {labeling
                                ? <LabelForm
                                    datasetId={datasetId}
                                    input={row.input}
                                    onSaved={() => { setLabeling(false); onChanged(); }}
                                />
                                : row.input.label
                                    ? <LabelPanel row={row} />
                                    : <p className="bench-muted">
                                        Unlabeled. Verdicts still work without one — a label is what lets the
                                        scorer grade runs of this row on its own.
                                    </p>}
                        </div>
                    </aside>

                    <main className="bench-chains">
                        {!row.chains.length && <p className="bench-muted">No runs yet for this row.</p>}
                        {row.chains.map((chain, i) => (
                            <ChainPanel
                                key={`${chain.runId ?? 'hist'}-${chain.pipelineName}-${i}`}
                                datasetId={datasetId}
                                row={row}
                                chain={chain}
                                focusOutputId={focusOutputId}
                                onChanged={onChanged}
                            />
                        ))}
                    </main>
                </div>
            </div>

            {zoomed && (
                <div className="bench-lightbox" onClick={e => { e.stopPropagation(); setZoomed(null); }}>
                    <img src={zoomed} alt="student solution, full size" onClick={e => e.stopPropagation()} />
                    <button type="button" className="btn btn-secondary bench-lightbox-close"
                        onClick={() => setZoomed(null)}>Close</button>
                </div>
            )}
        </div>
    );
}

function LabelPanel({ row }: { row: MatrixRow }) {
    const a = row.input.label?.assessment as Record<string, unknown> | undefined;
    if (!a) return null;
    return <pre className="bench-label-json">{JSON.stringify(a, null, 1)}</pre>;
}

// ─── One chain ─────────────────────────────────────────────────────────

function ChainPanel({ datasetId, row, chain, focusOutputId, onChanged }: {
    datasetId: string; row: MatrixRow; chain: MatrixChain; focusOutputId?: string; onChanged: () => void;
}) {
    const label = chain.pipelineConfigName ?? chain.pipelineName;
    const isHistorical = chain.cells.some(c => c.historical);

    return (
        <section className="bench-chain-panel">
            <header>
                <h3>{label}</h3>
                <span className="bench-muted">{chain.pipelineName}</span>
                {isHistorical && (
                    <span className="bench-pill warn" title="Produced in production, under whatever prompt version shipped that day — not a run this bench launched. Judgeable, and its verdicts are kept apart from replayed ones rather than dropped: they score the analysis students actually received.">
                        shipped
                    </span>
                )}
                {chain.startedAt && <span className="bench-muted">{chain.startedAt.slice(0, 16).replace('T', ' ')}</span>}
            </header>

            <div className="bench-stages">
                {chain.cells.map(cell => (
                    <StagePanel
                        key={cell.outputId}
                        datasetId={datasetId}
                        row={row}
                        cell={cell}
                        runId={chain.runId}
                        defaultOpen={cell.outputId === focusOutputId}
                        onChanged={onChanged}
                    />
                ))}
            </div>
        </section>
    );
}

// ─── One stage = one prompt execution = one verdict ────────────────────

function StagePanel({ datasetId, row, cell, runId, defaultOpen, onChanged }: {
    datasetId: string; row: MatrixRow; cell: MatrixCell; runId?: string; defaultOpen?: boolean; onChanged: () => void;
}) {
    const [open, setOpen] = useState(!!defaultOpen);
    const human = cell.humanVerdict;

    const [verdict, setVerdict] = useState<VerdictValue | ''>(human?.verdict ?? '');
    const [conditional, setConditional] = useState<VerdictValue | ''>(human?.conditionalVerdict ?? '');
    const [tags, setTags] = useState<Set<string>>(new Set(human?.failureTags ?? []));
    const [critique, setCritique] = useState(human?.critique ?? '');

    // The output body is only fetched when a stage is expanded — a row can hold
    // dozens of cells and each carries a full assessment payload.
    const { data: output, isFetching } = useQuery({
        queryKey: ['modelOutput', cell.outputId],
        queryFn: () => adminApi.getModelOutputById(cell.outputId),
        enabled: open,
    });

    const save = useMutation({
        mutationFn: () => adminApi.putBenchVerdict(datasetId, row.input.id, cell.outputId, {
            verdict: verdict as VerdictValue,
            ...(conditional ? { conditionalVerdict: conditional as VerdictValue } : {}),
            failureTags: [...tags],
            ...(critique ? { critique } : {}),
            ...(runId ? { runId } : {}),
        }),
        onSuccess: onChanged,
    });

    const clear = useMutation({
        mutationFn: () => adminApi.deleteBenchVerdict(datasetId, row.input.id, cell.outputId),
        onSuccess: () => { setVerdict(''); setConditional(''); setTags(new Set()); setCritique(''); onChanged(); },
    });

    const toggleTag = (t: string) => {
        const next = new Set(tags);
        next.has(t) ? next.delete(t) : next.add(t);
        setTags(next);
    };

    return (
        <div className={`bench-stage role-${cell.role}${open ? ' open' : ''}`}>
            <button type="button" className="bench-stage-head" onClick={() => setOpen(o => !o)}>
                <span className="bench-stage-role">{cell.role}</span>
                <span className="bench-stage-type">{cell.stepType}</span>
                <span className="bench-stage-model">{cell.provider ?? '—'}</span>
                {cell.promptId && <span className="bench-muted">{cell.promptId.slice(0, 8)}@v{cell.promptVersion}</span>}
                {cell.latencyMs !== undefined && <span className="bench-muted">{Math.round(cell.latencyMs)}ms</span>}
                {cell.costUsd !== undefined && <span className="bench-muted">${cell.costUsd.toFixed(4)}</span>}
                {human && <span className={`bench-verdict-badge v-${human.verdict}`}>{human.verdict}</span>}
                {cell.judgeVerdict && <span className={`bench-verdict-badge judge j-${cell.judgeVerdict.verdict}`}>⚖ {cell.judgeVerdict.verdict}</span>}
                {cell.staleVerdict && <span className="bench-pill warn" title="This verdict was recorded against an older version of the row.">stale</span>}
                <span className="bench-stage-caret">{open ? '▾' : '▸'}</span>
            </button>

            {open && (
                <div className="bench-stage-body">
                    <div className="bench-output">
                        {isFetching && <p className="bench-muted">Loading output…</p>}
                        {/* The endpoint answers `{ output, input }`, not a bare
                            output — reading a level too shallow emptied every
                            panel on the screen regardless of what actually ran. */}
                        {output?.output && (
                            <StageOutput role={cell.role} output={output.output as unknown as Record<string, unknown>} />
                        )}
                    </div>

                    <div className="bench-verdict-form">
                        <div className="bench-verdict-row">
                            <span className="bench-form-label">Verdict</span>
                            {(['good', 'acceptable', 'bad'] as VerdictValue[]).map(v => (
                                <button key={v} type="button"
                                    className={`bench-vbtn v-${v}${verdict === v ? ' on' : ''}`}
                                    onClick={() => setVerdict(v)}>{v}</button>
                            ))}
                        </div>

                        {(cell.role === 'assessment' || cell.role === 'scoring') && (
                            <div className="bench-verdict-row">
                                <span className="bench-form-label" title="Was this step right GIVEN the transcription it was handed? High here with a low absolute verdict means the reading step is the problem, not this model.">
                                    Given its input
                                </span>
                                {(['good', 'acceptable', 'bad'] as VerdictValue[]).map(v => (
                                    <button key={v} type="button"
                                        className={`bench-vbtn v-${v}${conditional === v ? ' on' : ''}`}
                                        onClick={() => setConditional(conditional === v ? '' : v)}>{v}</button>
                                ))}
                            </div>
                        )}

                        <div className="bench-tagpick">
                            {tagsForRole(cell.role).map(t => (
                                <button key={t} type="button" className={`bench-tagbtn${tags.has(t) ? ' on' : ''}`}
                                    onClick={() => toggleTag(t)}>{t}</button>
                            ))}
                        </div>

                        <textarea
                            className="bench-critique"
                            placeholder="Why — the specific thing this step got right or wrong"
                            value={critique}
                            onChange={e => setCritique(e.target.value)}
                            rows={3}
                        />

                        {save.error && <p className="bench-error">{String(save.error)}</p>}

                        <div className="bench-verdict-actions">
                            {human && (
                                <button className="btn btn-secondary" disabled={clear.isPending} onClick={() => clear.mutate()}>
                                    Clear
                                </button>
                            )}
                            <button className="btn btn-primary" disabled={!verdict || save.isPending} onClick={() => save.mutate()}>
                                {save.isPending ? 'Saving…' : save.isSuccess ? 'Saved' : 'Save verdict'}
                            </button>
                        </div>

                        {cell.judgeVerdict?.critique && (
                            <div className="bench-judge-note">
                                <strong>⚖ Judge:</strong> {cell.judgeVerdict.critique}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

/**
 * What a step produced, shown on its own terms: an extraction step's product is
 * the transcription, an analysis step's is the assessment. Showing the final
 * assessment under an extraction cell is what made reading failures invisible.
 */
function StageOutput({ role, output }: { role: StepRole; output: Record<string, unknown> }) {
    const extracted = output.extractedData;
    const assessment = (output.assessmentDetails ?? output.parsedAssessment) as Record<string, unknown> | undefined;
    const stepInput = output.stepInput;

    if (role === 'reading') {
        return (
            <>
                <h5>Transcription produced</h5>
                {extracted
                    ? <pre>{JSON.stringify(extracted, null, 1)}</pre>
                    : <p className="bench-muted">No <code>extractedData</code> on this output.</p>}
            </>
        );
    }

    return (
        <>
            {stepInput != null && (
                <details className="bench-stepinput">
                    <summary>Input it received from the previous step</summary>
                    <pre>{JSON.stringify(stepInput, null, 1)}</pre>
                </details>
            )}
            <h5>{role === 'scoring' ? 'Computed scores' : 'Assessment produced'}</h5>
            {assessment
                ? <pre>{JSON.stringify(assessment, null, 1)}</pre>
                : <pre>{String(output.rawOutput ?? '').slice(0, 4000)}</pre>}
        </>
    );
}

export default BenchRowView;
